import axios, { AxiosInstance } from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';

/**
 * Client API Service per chiamate come client del wrapper
 * (non come admin)
 */
class ClientApiService {
  private api: AxiosInstance;
  private token: string | null = null;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Interceptor per aggiungere il token a ogni richiesta.
    this.api.interceptors.request.use((config) => {
      const token = this.token || localStorage.getItem('client_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Carica token salvato
    const savedToken = localStorage.getItem('client_token');
    if (savedToken) {
      this.token = savedToken;
    }
  }

  /**
   * Login with username and password
   */
  async login(username: string, password: string) {
    try {
      const response = await this.api.post('/auth/login', {
        username,
        password,
      });

      this.token = response.data.token;
      if (this.token) {
        localStorage.setItem('client_token', this.token);
      }
      // Store user info
      if (response.data.user) {
        localStorage.setItem('user_info', JSON.stringify(response.data.user));
        localStorage.setItem('user_name', response.data.user.full_name || response.data.user.username);
        // Store as portal_name for compatibility with Search page
        localStorage.setItem('portal_name', response.data.user.full_name || response.data.user.username);
      }

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Login failed');
    }
  }

  /**
   * Richiede il link di reset password via email.
   */
  async forgotPassword(email: string) {
    const response = await this.api.post('/auth/forgot-password', { email });
    return response.data;
  }

  /**
   * Imposta una nuova password usando il token ricevuto via email.
   */
  async resetPassword(token: string, password: string) {
    const response = await this.api.post('/auth/reset-password', { token, password });
    return response.data;
  }

  /**
   * Autentica con API Key e Secret (for portals)
   */
  async authenticate(apiKey: string, apiSecret: string) {
    try {
      const response = await this.api.post('/auth/token', {
        api_key: apiKey,
        api_secret: apiSecret,
      });

      this.token = response.data.token;
      if (this.token) {
        localStorage.setItem('client_token', this.token);
      }
      localStorage.setItem('portal_name', response.data.portal.name);

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Authentication failed');
    }
  }

  /**
   * Logout — revoke the token server-side, then clear local state.
   */
  async logout() {
    // Revoke the token on the server (best-effort) while it is still set, so the
    // interceptor can attach it. Always clear local state even if this fails.
    try {
      if (this.token) {
        await this.api.post('/auth/logout');
      }
    } catch {
      // ignore network/revocation errors — local cleanup below still runs
    }
    this.token = null;
    localStorage.removeItem('client_token');
    localStorage.removeItem('portal_name');
    localStorage.removeItem('user_info');
    localStorage.removeItem('user_name');
  }

  /**
   * Check se autenticato. Coerente con l'interceptor: vale anche il token admin,
   * cosi un admin puo usare la ricerca (il bottone non resta disabilitato).
   */
  isAuthenticated(): boolean {
    return !!(this.token || localStorage.getItem('client_token'));
  }

  /**
   * Get portal name
   */
  getPortalName(): string | null {
    return localStorage.getItem('portal_name');
  }

  /**
   * Get user info
   */
  getUserInfo(): any | null {
    const userInfo = localStorage.getItem('user_info');
    return userInfo ? JSON.parse(userInfo) : null;
  }

  /**
   * Get user name
   */
  getUserName(): string | null {
    return localStorage.getItem('user_name');
  }

  /**
   * Search comparables by attribute
   */
  async searchByAttribute(filters: any, userReference?: string, page?: number, perPage?: number) {
    const body: any = { Filters: filters };
    if (userReference) {
      body.user_reference = userReference;
    }
    // Upstream API uses 1-based indexing (page 1 = first page)
    // pageSize is fixed at 8 per page by the API
    if (page !== undefined) {
      body.page = page;  // Keep 1-based indexing
    }
    if (perPage !== undefined) {
      body.per_page = perPage;  // Sent but API uses fixed pageSize of 8
    }

    const response = await this.api.post('/comparables/searchByAttribute', body);
    return response.data;
  }

  /**
   * Full search comparables by attribute
   */
  async fullSearchByAttribute(filters: any, userReference?: string) {
    const body: any = { Filters: filters };
    if (userReference) {
      body.user_reference = userReference;
    }

    const response = await this.api.post('/comparables/fullSearchByAttribute', body);
    return response.data;
  }

  /**
   * Search comparables by property
   */
  async searchByProperty(cadastralCode: string, sheet: string, particle: string, userReference?: string) {
    const body: any = {
      cadastral_code: cadastralCode,
      sheet,
      particle,
    };
    if (userReference) {
      body.user_reference = userReference;
    }

    const response = await this.api.post('/comparables/searchByProperty', body);
    return response.data;
  }

  /**
   * Get comparable details
   */
  async getComparable(uuid: string) {
    const response = await this.api.get(`/comparable/${uuid}`);
    return response.data;
  }

  /**
   * Get taxonomies
   */
  async getTaxonomies(type: string = 'general', params?: any) {
    const response = await this.api.get(`/taxonomies/${type}`, { params });
    return response.data;
  }

  /**
   * Search market zones by city or postal code
   */
  async searchMarketZones(query: string) {
    const response = await this.api.get('/taxonomies/search-zones', { params: { query } });
    return response.data;
  }

  /**
   * Geocode address to coordinates
   */
  async geocodeAddress(address: string) {
    const response = await this.api.get('/geocoding/search', { params: { query: address } });
    return response.data;
  }

  /**
   * Reverse geocode coordinates to address
   */
  async reverseGeocode(lat: number, lng: number) {
    const response = await this.api.get('/geocoding/reverse', { params: { lat, lng } });
    return response.data;
  }
}

export default new ClientApiService();
